"use client";

import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

export interface MapCut {
  enzyme: string;
  /** Cut after this base (top strand), 1 to length. */
  position: number;
  colorIndex: number;
}

const WIDTH = 720;
const LABEL_ROWS = 4;

function color(index: number): string {
  return CHART_COLORS[index % (CHART_COLORS.length - 1)];
}

/** Cuts that fall close together get labels on different rows so they stay legible. */
function rowsFor(cuts: readonly MapCut[]): number[] {
  return cuts.map((_, index) => index % LABEL_ROWS);
}

function LinearMap({
  length,
  cuts,
}: {
  length: number;
  cuts: readonly MapCut[];
}) {
  const left = 24;
  const right = WIDTH - 24;
  const barY = 24 + LABEL_ROWS * 14;
  const scale = (position: number) =>
    left + (position / length) * (right - left);
  const rows = rowsFor(cuts);
  return (
    <>
      <line
        stroke={CHART_INK.axis}
        strokeWidth={4}
        x1={left}
        x2={right}
        y1={barY}
        y2={barY}
      />
      {cuts.map((cut, index) => {
        const x = px(scale(cut.position));
        const labelY = 14 + rows[index] * 14;
        return (
          <g key={`${cut.enzyme}-${cut.position}`}>
            <line
              stroke={color(cut.colorIndex)}
              strokeWidth={1.5}
              x1={x}
              x2={x}
              y1={labelY + 3}
              y2={barY + 8}
            />
            <text
              fill={color(cut.colorIndex)}
              fontSize={11}
              textAnchor="middle"
              x={x}
              y={labelY}
            >
              {cut.enzyme}
            </text>
          </g>
        );
      })}
      <text fill={CHART_INK.muted} fontSize={11} x={left} y={barY + 26}>
        1
      </text>
      <text
        fill={CHART_INK.muted}
        fontSize={11}
        textAnchor="end"
        x={right}
        y={barY + 26}
      >
        {length}
      </text>
    </>
  );
}

function anchorFor(x: number, centre: number): "start" | "middle" | "end" {
  if (x < centre - 4) {
    return "end";
  }
  return x > centre + 4 ? "start" : "middle";
}

function CircularMap({
  length,
  cuts,
}: {
  length: number;
  cuts: readonly MapCut[];
}) {
  const cx = WIDTH / 2;
  const cy = 170;
  const radius = 100;
  const rows = rowsFor(cuts);
  const point = (position: number, r: number) => {
    const angle = (position / length) * 2 * Math.PI - Math.PI / 2;
    return [
      px(cx + r * Math.cos(angle)),
      px(cy + r * Math.sin(angle)),
    ] as const;
  };
  return (
    <>
      <circle
        cx={cx}
        cy={cy}
        fill="none"
        r={radius}
        stroke={CHART_INK.axis}
        strokeWidth={4}
      />
      <text
        fill={CHART_INK.muted}
        fontSize={11}
        textAnchor="middle"
        x={cx}
        y={cy - radius - 26}
      >
        1
      </text>
      {cuts.map((cut, index) => {
        const [x1, y1] = point(cut.position, radius - 6);
        const [x2, y2] = point(cut.position, radius + 8);
        const [lx, ly] = point(cut.position, radius + 20 + rows[index] * 10);
        const anchor = anchorFor(lx, cx);
        return (
          <g key={`${cut.enzyme}-${cut.position}`}>
            <line
              stroke={color(cut.colorIndex)}
              strokeWidth={1.5}
              x1={x1}
              x2={x2}
              y1={y1}
              y2={y2}
            />
            <text
              fill={color(cut.colorIndex)}
              fontSize={11}
              textAnchor={anchor}
              x={lx}
              y={ly + 4}
            >
              {cut.enzyme}
            </text>
          </g>
        );
      })}
      <text
        fill={CHART_INK.text}
        fontSize={13}
        textAnchor="middle"
        x={cx}
        y={cy + 4}
      >
        {length}
      </text>
    </>
  );
}

export function RestrictionMap({
  id,
  length,
  circular,
  cuts,
  label,
  description,
}: {
  id: string;
  length: number;
  circular: boolean;
  cuts: readonly MapCut[];
  label: string;
  description: string;
}) {
  const height = circular ? 340 : 24 + LABEL_ROWS * 14 + 44;
  return (
    <ChartSvg
      description={description}
      height={height}
      id={id}
      label={label}
      width={WIDTH}
    >
      {circular ? (
        <CircularMap cuts={cuts} length={length} />
      ) : (
        <LinearMap cuts={cuts} length={length} />
      )}
    </ChartSvg>
  );
}
